import type { LocalDateTime } from "@js-joda/core";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { BmTask } from "@/app/services/generated/bmtaskService";
import {
  bmTaskService,
  BmTaskFilter,
} from "@/app/services/generated/bmtaskService";
import {
  withLoading,
  wrapPromiseWithErrorHandling,
  makeRef,
  wrapLoading,
} from "@/components/composables/componentTools";
import { EnumTaskStatus } from "@/app/services/generated/enums/enumTaskStatus.generated";
import type { Ref } from "vue";
import { computed, watch } from "vue";
import { GuidSet } from "@/app/types/common/guidcollections";
import Guid from "@/app/types/common/guid";
import type { TaskWithAssociations } from "@/components/pages/tasks/composables/taskAssociations";
import { joinTasksWithAssociations } from "@/components/pages/tasks/composables/taskAssociations";
import tasksStore from "@/store/modules/tasksStore";
import { distinctGuids, toGuidMap } from "@/app/apiutils";
import type { Contact } from "@/app/services/generated/outletService";
import { outletService } from "@/app/services/generated/outletService";
import { activityLogService } from "@/app/services/generated/activitylogService";
import type { ActivityLogEntry } from "@/app/services/generated/activitylogService";
import { EnumBmEntityType } from "@/app/services/generated/enums/enumBmEntityType.generated";
import { EnumActivityLogType } from "@/app/services/generated/enums/enumActivityLogType.generated";
import { PiecemealByGuidsCache } from "@/app/caching/piecemealByGuidsCache";
import { PiecemealQByGuidsCache } from "@/app/caching/piecemealQByGuidsCache";
import { buildCache } from "@/app/caching/buildCache";
import type { DateRange } from "@/components/controls/daterange/dateRangePresets";
import type { OrderSummary } from "@/app/services/generated/orderService";
import { orderService } from "@/app/services/generated/orderService";

export const PLACEHOLDER_USER_ID = Guid.createEmpty();

/**
 * Determines if the supplied task is overdue in respect to the supplied datetime.
 *
 * `now` would most likely be: LocalDateTime.now(tenantService.getTimezone());
 */
export function isTaskOverdue(task: BmTask, now: LocalDateTime) {
  return (
    !!task.dueDate &&
    (task.dueDate.isBefore(now.toLocalDate()) ||
      (!!task.dueTime &&
        task.dueDate.equals(now.toLocalDate()) &&
        task.dueTime.isBefore(now.toLocalTime())))
  );
}

/**
 * Determines if the task is required to be done on the same day
 * that `now` references
 *
 * We expect not overdue tasks, with no due date, to be in the todo section
 */
export function isTaskInToDoSection(task: BmTask, now: LocalDateTime): boolean {
  if (task.dueDate) {
    if (task.dueTime) {
      return (
        task.dueDate.isEqual(now.toLocalDate()) &&
        task.dueTime.isAfter(now.toLocalTime())
      );
    }

    return task.dueDate.isEqual(now.toLocalDate());
  }

  return true;
}

export function useTasksList(dueDateRange: Ref<DateRange | null>) {
  const cache = buildCache()
    .withDeliveryAreas()
    .withGroups()
    .withOutletRatings()
    .withSalesAreas()
    .withSalesCodes()
    .withTaskQueues()
    .withTaskTypes()
    .build();

  const selectedTaskTypeIds = makeRef<Guid[]>([]);
  const selectedTaskQueueIds = makeRef<Guid[]>([]);
  const selectedUserIds = makeRef<Guid[]>([]);

  const openTaskRows = makeRef<readonly TaskWithAssociations[]>([]);
  const closedTasks = makeRef<readonly TaskWithAssociations[]>([]);

  // Is our closed tasks card open?
  const closedTasksVisible = makeRef(false);

  // Some Piecemeal caches for dependant data
  const outletContactsLookup = new PiecemealByGuidsCache<Contact>(
    (x) => x.outletId,
    (outletIds) => outletService.getPrimaryContactsForOutlets(outletIds)
  );

  const outletsRecentActivityLogCache =
    new PiecemealByGuidsCache<ActivityLogEntry>(
      (x) => x.activityOnEntityId,
      (outletIds) =>
        activityLogService.getMostRecentActivityLogEntries(
          EnumActivityLogType.communication,
          EnumBmEntityType.outlet,
          outletIds
        )
    );

  const leadsRecentActivityLogCache =
    new PiecemealByGuidsCache<ActivityLogEntry>(
      (x) => x.activityOnEntityId,
      (leadIds) =>
        activityLogService.getMostRecentActivityLogEntries(
          EnumActivityLogType.communication,
          EnumBmEntityType.lead,
          leadIds
        )
    );

  const outletNextOpenOrdersLookup = new PiecemealQByGuidsCache<OrderSummary>(
    (x) => x.outletId,
    (outletIds) => orderService.getNextOpenOrdersForOutlets(outletIds)
  );

  const recentActivityLogEntriesLookup = computed(() =>
    toGuidMap(
      outletsRecentActivityLogCache
        .values()
        .concat(leadsRecentActivityLogCache.values()),
      (x) => x.activityOnEntityId
    )
  );

  // The most common use of 'now', for ease of use
  let dateTimeNow = tenantService.nowDateTime;

  const refreshOpenTasks = withLoading(async () => {
    dateTimeNow = tenantService.nowDateTime;

    const taskFilter = new BmTaskFilter();
    taskFilter.limitStatuses = [EnumTaskStatus.open];

    taskFilter.fromDueDate = dueDateRange.value?.from ?? null;
    taskFilter.toDueDate = dueDateRange.value?.to ?? null;

    const openTasks = (await bmTaskService.getBmTasksByFilter(taskFilter))
      .bmTasks;

    openTaskRows.value = await joinTasksWithAssociations(openTasks);

    return openTasks;
  });

  const refreshClosedTasks = async () => {
    dateTimeNow = tenantService.nowDateTime;

    const taskFilter = new BmTaskFilter();

    taskFilter.limitStatuses = [
      EnumTaskStatus.cancelled,
      EnumTaskStatus.complete,
    ];

    taskFilter.fromDueDate = dueDateRange.value?.from ?? null;
    taskFilter.toDueDate = dueDateRange.value?.to ?? null;

    taskFilter.limitAssignedUserIds = selectedUserIds.value.length
      ? selectedUserIds.value.map((x) =>
          x.equals(PLACEHOLDER_USER_ID) ? null : x
        )
      : null;

    taskFilter.limitTaskTypeIds = selectedTaskTypeIds.value.length
      ? selectedTaskTypeIds.value
      : null;

    taskFilter.limitTaskQueueIds = selectedTaskQueueIds.value.length
      ? selectedTaskQueueIds.value
      : null;

    taskFilter.maximumResults = 1000;

    const tasks = (await bmTaskService.getBmTasksByFilter(taskFilter)).bmTasks;

    closedTasks.value = await joinTasksWithAssociations(tasks);
  };

  const refreshDependencies = async () => {
    const outletIds = distinctGuids(
      openTaskRows.value
        .concat(closedTasks.value)
        .map((x) => x.associatedOutlet?.id)
    );

    const leadIds = distinctGuids(
      openTaskRows.value
        .concat(closedTasks.value)
        .map((x) => x.associatedLead?.id)
    );

    await Promise.all([
      outletContactsLookup.ensureLoaded(outletIds),
      outletsRecentActivityLogCache.ensureLoaded(outletIds),
      leadsRecentActivityLogCache.ensureLoaded(leadIds),
      outletNextOpenOrdersLookup.ensureLoaded(outletIds),
    ]);
  };

  const cancelTask = withLoading(async (task: BmTask) => {
    await bmTaskService.cancelBmTask(task.id);
    tasksStore.refresh();
    await refreshTasksAndDependencies();
  });

  const completeTask = withLoading(async (task: BmTask) => {
    await bmTaskService.completeBmTask(task.id);
    tasksStore.refresh();
    await refreshTasksAndDependencies();
  });

  const refreshTasksAndDependencies = async () => {
    await wrapLoading(async () => {
      await Promise.all([
        cache.update(),
        refreshOpenTasks(),
        closedTasksVisible.value ? refreshClosedTasks() : null,
      ]);

      await refreshDependencies();
    });
  };

  const refreshJustClosedTasksAndDependencies = async () => {
    if (closedTasksVisible.value) {
      await wrapLoading(async () => {
        await Promise.all([cache.update(), refreshClosedTasks()]);

        await refreshDependencies();
      });
    }
  };

  const selectedOpenTasks = computed(() => {
    const typesHash = new GuidSet(selectedTaskTypeIds.value);
    const queuesHash = new GuidSet(selectedTaskQueueIds.value);
    const usersHash = new GuidSet(selectedUserIds.value);

    return openTaskRows.value.filter(
      (task) =>
        (!typesHash.size || typesHash.has(task.task.taskTypeId)) &&
        (!queuesHash.size || queuesHash.has(task.task.taskQueueId)) &&
        (!usersHash.size ||
          usersHash.has(task.task.assignedToUserId ?? PLACEHOLDER_USER_ID))
    );
  });

  // All open tasks that are not overdue, but due today
  const todoTasks = computed(() =>
    selectedOpenTasks.value.filter(
      (row) =>
        !isTaskOverdue(row.task, dateTimeNow) &&
        isTaskInToDoSection(row.task, dateTimeNow)
    )
  );

  // All open tasks that are not overdue, and also not due today
  const upcomingTasks = computed(() =>
    selectedOpenTasks.value.filter(
      (row) =>
        !isTaskOverdue(row.task, dateTimeNow) &&
        !isTaskInToDoSection(row.task, dateTimeNow)
    )
  );

  const overdueTasks = computed(() =>
    selectedOpenTasks.value.filter((row) =>
      isTaskOverdue(row.task, dateTimeNow)
    )
  );

  watch(closedTasksVisible, async () => {
    if (closedTasksVisible.value) {
      await wrapLoading(
        wrapPromiseWithErrorHandling(async () => refreshTasksAndDependencies())
      );
    } else {
      closedTasks.value = [];
    }
  });

  return {
    cache,
    outletContactsLookup,
    outletNextOpenOrdersLookup,
    recentActivityLogEntriesLookup,

    // Task lists
    todoTasks,
    upcomingTasks,
    overdueTasks,
    closedTasks,

    closedTasksVisible,

    // Filter items
    selectedTaskTypeIds,
    selectedTaskQueueIds,
    selectedUserIds,

    cancelTask,
    completeTask,
    refreshTasksAndDependencies,
    refreshJustClosedTasksAndDependencies,
  };
}
